import { useState, useCallback, useEffect, useRef } from "react";
import { projects } from "../../../mocks/projects";

type Orientation = "landscape" | "portrait" | "square";

const FIXED_HEIGHT = "clamp(200px, calc(100vh - 220px), 720px)";
const BASE_SPEED = 0.35; // velocidade base (px por frame a 60fps)

interface SlideshowProps {
  introComplete?: boolean;
}

export default function Slideshow({ introComplete }: SlideshowProps) {
  const [orientations, setOrientations] = useState<Record<number, Orientation>>({});
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    if (introComplete && !revealed) {
      const timer = setTimeout(() => setRevealed(true), 500);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [introComplete, revealed]);

  const detectOrientation = useCallback(
    (id: number, e: React.SyntheticEvent<HTMLImageElement>) => {
      const img = e.currentTarget;
      const ratio = img.naturalWidth / img.naturalHeight;
      let orientation: Orientation;
      if (ratio >= 0.9 && ratio <= 1.1) {
        orientation = "square";
      } else if (img.naturalWidth >= img.naturalHeight) {
        orientation = "landscape";
      } else {
        orientation = "portrait";
      }
      setOrientations((prev) => ({ ...prev, [id]: orientation }));
    },
    []
  );

  const portraitAlignSequence: ("left" | "right")[] = ["right", "left", "right", "left", "right", "left"];
  const getPortraitAlign = (index: number): "left" | "right" =>
    portraitAlignSequence[index % portraitAlignSequence.length];

  // Imagens 4 (índice 0-based: 3) ficam sempre alinhadas à direita
  const FORCE_RIGHT = new Set([3]);
  // Imagens 5, 9 e 13 (índice 0-based: 4, 8, 12) ficam sempre alinhadas à esquerda
  const FORCE_LEFT = new Set([4, 8, 12]);
  // Imagens 6 e 10 (índice 0-based: 5, 9) ficam sempre centradas
  const FORCE_CENTER = new Set([5, 9]);

  const scrollItems = [...projects, ...projects];

  // ── JS-driven infinite scroll ──
  const trackRef = useRef<HTMLDivElement>(null);
  const posRef = useRef(0);
  const velocityRef = useRef(BASE_SPEED);
  const pausedRef = useRef(false);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    const getHalfHeight = () => {
      if (!trackRef.current) return 0;
      return trackRef.current.scrollHeight / 2;
    };

    const animate = () => {
      if (!pausedRef.current) {
        // Lerp velocity de volta à velocidade base
        velocityRef.current += (BASE_SPEED - velocityRef.current) * 0.03;

        const halfH = getHalfHeight();
        if (halfH > 0) {
          posRef.current += velocityRef.current;
          // wrap infinito nos dois sentidos
          if (posRef.current >= halfH) posRef.current -= halfH;
          if (posRef.current < 0) posRef.current += halfH;

          if (trackRef.current) {
            trackRef.current.style.transform = `translateY(-${posRef.current}px)`;
          }
        }
      }

      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  const handleWheel = useCallback((e: React.WheelEvent<HTMLDivElement>) => {
    e.preventDefault();

    // Despausa se estiver pausado
    if (pausedRef.current) {
      pausedRef.current = false;
      velocityRef.current = 0;
    }

    // deltaY positivo = scroll para baixo (avança), negativo = scroll para cima (recua)
    const boost = e.deltaY * 0.08;
    velocityRef.current = Math.max(-20, Math.min(20, velocityRef.current + boost));
  }, []);

  const handleImageClick = useCallback(() => {
    pausedRef.current = true;
    velocityRef.current = 0;
  }, []);

  const getImageStyle = (id: number): React.CSSProperties => {
    const o = orientations[id] ?? "landscape";
    if (o === "portrait") {
      return { height: FIXED_HEIGHT, width: "auto", aspectRatio: "3 / 4" };
    }
    if (o === "square") {
      return { height: FIXED_HEIGHT, width: "auto", aspectRatio: "1 / 1" };
    }
    return { height: FIXED_HEIGHT, width: "auto", aspectRatio: "4 / 3" };
  };

  // ── curtain reveal helpers (primeiras 2 imagens) ──
  const getCurtainWrapStyle = (index: number): React.CSSProperties => {
    if (index > 1) return {};
    const delay = index === 0 ? "0ms" : "80ms";
    return {
      clipPath: revealed ? "inset(0% 0 0 0)" : "inset(100% 0 0 0)",
      transition: `clip-path 0.75s cubic-bezier(0.76, 0, 0.24, 1) ${delay}`,
    };
  };

  const getCurtainImgStyle = (index: number, base: React.CSSProperties): React.CSSProperties => {
    if (index > 1) return base;
    const delay = index === 0 ? "0ms" : "80ms";
    return {
      ...base,
      transform: revealed ? "scale(1)" : "scale(1.09)",
      transition: `transform 0.85s cubic-bezier(0.76, 0, 0.24, 1) ${delay}`,
    };
  };

  const getCaptionStyle = (index: number): React.CSSProperties => {
    if (index > 1) return {};
    const delay = index === 0 ? "420ms" : "500ms";
    return {
      opacity: revealed ? 1 : 0,
      transition: `opacity 0.55s ease ${delay}`,
    };
  };

  return (
    <section className="flex-1 relative overflow-hidden">
      {/* ── DESKTOP: auto-scroll (lg+) ── */}
      <div
        className="hidden lg:flex items-start justify-center h-full overflow-hidden"
        onWheel={handleWheel}
      >
        <div
          ref={trackRef}
          className="flex flex-col gap-12 pt-28 pb-16"
          style={{ willChange: "transform" }}
        >
          {scrollItems.map((p, i) => {
            const orientation = orientations[p.id] ?? "landscape";
            const isPortrait = orientation === "portrait";
            const originalIndex = i % projects.length;
            const forceRight = FORCE_RIGHT.has(originalIndex);
            const forceLeft = FORCE_LEFT.has(originalIndex);
            const forceCenter = FORCE_CENTER.has(originalIndex);
            const align: "left" | "right" | "center" = isPortrait
              ? (forceRight ? "right" : forceLeft ? "left" : forceCenter ? "center" : getPortraitAlign(originalIndex))
              : "left";
            const justifyClass = align === "right" ? "justify-end" : align === "center" ? "justify-center" : "justify-start";
            const textAlignClass = align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left";

            return (
              <div key={`${p.id}-${i}`} className={`flex flex-col ${justifyClass}`}>
                <div
                  className={`flex ${justifyClass} overflow-hidden cursor-pointer`}
                  style={getCurtainWrapStyle(i)}
                  onClick={handleImageClick}
                >
                  <img
                    src={p.image}
                    alt={p.name}
                    onLoad={(e) => detectOrientation(p.id, e)}
                    draggable={false}
                    className="block object-cover select-none"
                    style={getCurtainImgStyle(i, getImageStyle(p.id))}
                  />
                </div>
                <p
                  className={`mt-3 text-[10px] ${textAlignClass}`}
                  style={{
                    fontFamily: "'Inter', sans-serif",
                    letterSpacing: "normal",
                    ...getCaptionStyle(i),
                  }}
                >
                  <span style={{ fontWeight: 500, color: "#44403c" }}>{p.year}&nbsp;&nbsp;</span>
                  <span style={{ fontWeight: 500, color: "#000000" }}>{p.name}</span>
                  <span style={{ fontWeight: 500, color: "#44403c" }}>,&nbsp;{p.location}</span>
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── MOBILE / TABLET: stacked (below lg) ── */}
      <div className="lg:hidden flex flex-col gap-14 px-4 pt-4">
        {projects.map((p, i) => {
          const orientation = orientations[p.id] ?? "landscape";
          const isPortrait = orientation === "portrait";
          const forceRight = FORCE_RIGHT.has(i);
          const forceLeft = FORCE_LEFT.has(i);
          const forceCenter = FORCE_CENTER.has(i);
          const align: "left" | "right" | "center" = isPortrait
            ? (forceRight ? "right" : forceLeft ? "left" : forceCenter ? "center" : getPortraitAlign(i))
            : "left";
          const justifyImg =
            align === "left" ? "justify-start" : align === "right" ? "justify-end" : "justify-center";
          const textAlign =
            align === "left" ? "text-left" : align === "right" ? "text-right" : "text-center";

          return (
            <div key={p.id} className="flex flex-col">
              <div
                className={`overflow-hidden w-full flex items-center ${justifyImg}`}
                style={{
                  ...(isPortrait ? undefined : { height: "260px" }),
                  ...getCurtainWrapStyle(i),
                }}
              >
                <img
                  src={p.image}
                  alt={p.name}
                  onLoad={(e) => detectOrientation(p.id, e)}
                  className={isPortrait ? "w-3/4 h-auto block" : "h-full w-auto block"}
                  style={getCurtainImgStyle(i, {})}
                />
              </div>
              <p
                className={`mt-3 text-[10px] ${textAlign}`}
                style={{
                  fontFamily: "'Inter', sans-serif",
                  letterSpacing: "normal",
                  ...getCaptionStyle(i),
                }}
              >
                <span style={{ fontWeight: 400, color: "#44403c" }}>{p.year}&nbsp;&nbsp;</span>
                <span style={{ fontWeight: 500, color: "#000000" }}>{p.name}</span>
                <span style={{ fontWeight: 400, color: "#44403c" }}>,&nbsp;{p.location}</span>
              </p>
            </div>
          );
        })}

        {/* Mobile footer */}
        <div className="flex flex-col items-center justify-center pt-10 pb-32">
          <p
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: "10px",
              fontWeight: 400,
              color: "#78716c",
              textAlign: "center",
              lineHeight: "1.8",
            }}
          >
            © ECOS 3D<br />Todos os direitos reservados
          </p>
        </div>
      </div>
    </section>
  );
}
